import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { Agency, AgencyUpdate, AgencyConfig } from '../types'
import { generateColorPalette } from '@/shared/utils/colorPalette'

const AGENCIES_QUERY_KEY = ['agencies']

/**
 * Fetch the current user's agency
 */
export function useAgency() {
  return useQuery({
    queryKey: [...AGENCIES_QUERY_KEY, 'current'],
    queryFn: async () => {
      // Get current user
      const {
        data: { user },
      } = await supabase.auth.getUser()

      // Return null instead of throwing when not authenticated
      // This prevents keeping old data in cache
      if (!user) return null

      // Get user's agency_id
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('agency_id')
        .eq('id', user.id)
        .single()

      if (userError) throw userError
      if (!userData?.agency_id) return null

      // Get agency details
      const { data: agency, error: agencyError } = await supabase
        .from('agencies')
        .select('*')
        .eq('id', userData.agency_id)
        .single()

      if (agencyError) throw agencyError
      return agency as Agency
    },
    staleTime: 1000 * 60 * 5, // 5 minutes - balance between freshness and performance
    retry: false, // Don't retry if not authenticated
    refetchOnMount: true, // Always refetch when component mounts to get fresh data
  })
}

/**
 * Get agency configuration formatted for the app
 * Generates full color palette from the primary brand color
 */
export function useAgencyConfig() {
  const { data: agency, ...rest } = useAgency()

  const config: AgencyConfig | undefined = agency
    ? {
        id: agency.id,
        name: agency.name,
        slug: agency.slug,
        theme: {
          // Generate full palette from primary color (500 shade - median)
          brand: generateColorPalette(agency.brand_primary_color || '#10B981'),
        },
        assets: {
          logoCompactUrl: agency.logo_compact_url,
          logoInlineUrl: agency.logo_inline_url,
          loginBackgroundUrl: agency.login_background_url,
          showInlineLogoOnLogin: agency.show_inline_logo_on_login ?? true,
        },
      }
    : undefined

  return {
    data: config,
    ...rest,
  }
}

/**
 * Update agency configuration (admin only)
 */
export function useUpdateAgency() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...updates }: AgencyUpdate & { id: string }) => {
      const { data, error } = await supabase
        .from('agencies')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as Agency
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AGENCIES_QUERY_KEY })
    },
  })
}

/**
 * Upload agency asset (logo, background)
 */
export async function uploadAgencyAsset(
  agencyId: string,
  file: File,
  type: 'logo-compact' | 'logo-inline' | 'login-background'
): Promise<string> {
  const fileExt = file.name.split('.').pop()
  const fileName = `${type}.${fileExt}`
  const filePath = `agencies/${agencyId}/${fileName}`

  const { error: uploadError } = await supabase.storage
    .from('assets')
    .upload(filePath, file, {
      upsert: true,
      contentType: file.type,
    })

  if (uploadError) throw uploadError

  // Get public URL
  const {
    data: { publicUrl },
  } = supabase.storage.from('assets').getPublicUrl(filePath)

  return publicUrl
}
